import { Fragment, useCallback, useMemo, useState } from "react";

import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion/accordion";
import { Button } from "@/components/ui/button/button";

import { DisplaySettings } from "./display-settings";
import { Timeline, TimelinePanel } from "./timeline";
import { useCardDisplaySettingsLocalStorage, useHandlePrint } from "../../_hooks";
import * as UpdateMemoForOptionPms from "../../update-memo-for-option-pms";
import * as Model from "../model";

import type * as Repository from "../../_repository";

type Props = {
	hotelID: string;
	// オプションリスト
	optionPmsActiveListQueryData: Repository.Query.QueryData.OptionPmsList.OptionPmsActiveListQueryData;

	// print component
	printTimelineForOptionPmsChildren: React.ReactNode;
	searchDate: string | null;

	optionPmsColorListQueryData: Repository.Query.QueryData.OptionPmsColorListQueryData;
};

export const HasTimeLimitForOptionPms = ({
	hotelID,
	optionPmsActiveListQueryData,
	//
	printTimelineForOptionPmsChildren,
	searchDate,

	optionPmsColorListQueryData,
}: Props) => {
	const { settings, setShowSubOptionName, setShowGuestTotalNumber, setGroupByOptionID } =
		useCardDisplaySettingsLocalStorage(hotelID);

	/**
	 * 初期化
	 */
	const {
		//
		viewData,
		//
		// オプションの選択
		onSetCardIDOfOption,
		// オプションの選択解除
		onClearCardIDOfOption,
	} = Model.UseCase.useInitialized({
		hotelID,
		optionPmsActiveListQueryData,
		optionPmsColorListQueryData,
		groupByOptionID: settings.groupByOptionID,
	});

	const { onGenerateIdForPrintSectionEl, onPrint } = useHandlePrint();
	const idForPrintSectionEl = useMemo(
		() => onGenerateIdForPrintSectionEl({ prefix: "option-pms-has-time-limit" }),
		[ onGenerateIdForPrintSectionEl ],
	);
	const handleClickPrint = useCallback(() => {
		onPrint();
	}, [ onPrint ]);

	const optionListAccordionValue = "option-list";
	const [ accordionValue, setAccordionValue ] = useState(optionListAccordionValue);
	const handleAccordionValueChange = useCallback((value: string) => {
		setAccordionValue(value);
	}, []);

	return (
		<>
			<div className="space-y-3">
				<Accordion
					type="single"
					collapsible
					defaultValue={optionListAccordionValue}
					value={accordionValue}
					onValueChange={handleAccordionValueChange}
				>
					<AccordionItem value={optionListAccordionValue}>
						<AccordionTrigger>
							<p className="text-sm">時間指定のあるご予約</p>
						</AccordionTrigger>

						<AccordionContent className="p-4">
							<div className="space-y-7">
								<div className="flex justify-between gap-x-4">
									<div className="grid grid-cols-1 gap-2 tablet:grid-cols-2">
										{viewData.activeOptionColorList.map(({ color, optionNameText }, index) => (
											<div key={index} className="flex items-center gap-x-2">
												<label
													className="min-h-3 min-w-3 rounded-full"
													style={{ background: color }}
												/>

												<p className="line-clamp-3 text-xs">{optionNameText}</p>
											</div>
										))}
									</div>

									<Button variant="outline" type="button" onClick={handleClickPrint}>
										印刷
									</Button>
								</div>

								{/* カードの表示設定 */}
								<DisplaySettings
									showSubOptionName={settings.showSubOptionName}
									showGuestTotalNumber={settings.showGuestTotalNumber}
									groupByOptionID={settings.groupByOptionID}
									onChangeShowSubOptionName={setShowSubOptionName}
									onChangeShowGuestTotalNumber={setShowGuestTotalNumber}
									onChangeGroupByOptionID={setGroupByOptionID}
								/>

								{/* タイムライン */}
								<div className="max-h-[640px] overflow-y-auto rounded border p-3">
									<Timeline
										firstOptionStartAt={viewData.firstOptionStartAt}
										lastOptionEndAt={viewData.lastOptionEndAt}
										searchDate={searchDate}
										panelAreaMinWidth={viewData.panelAreaMinWidth}
										panelChildren={viewData.timelinePanelItemList.map((item, index) => {
											const isSelected = viewData.selectedOption?.cardID === item.cardId;

											return (
												<Fragment key={index}>
													<TimelinePanel
														cardId={item.cardId}
														guestName={item.guestName}
														memo={item.memo}
														subOptionName={item.subOptionName}
														guestTotalNumberText={item.guestTotalNumberText}
														showSubOptionName={settings.showSubOptionName}
														showGuestTotalNumber={settings.showGuestTotalNumber}
														color={item.color}
														height={item.height}
														top={item.top}
														left={item.left}
														onClickPanel={onSetCardIDOfOption}
													/>

													{isSelected ? (
														<UpdateMemoForOptionPms.UpdateMemoForOptionPms
															option={item.option}
															onCloseCallback={onClearCardIDOfOption}
														/>
													) : null}
												</Fragment>
											);
										})}
									/>
								</div>
							</div>
						</AccordionContent>
					</AccordionItem>
				</Accordion>
			</div>

			{/* print section */}
			<div id={idForPrintSectionEl} className="hidden">
				{printTimelineForOptionPmsChildren}
			</div>
		</>
	);
};
